import React from 'react'

function renderInline(text: string): React.ReactNode[] {
  return text.split(/(\[[^\]]+\]\(https:\/\/[^)\s]+\)|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g).filter(Boolean).map((part, index) => {
    const link = part.match(/^\[([^\]]+)\]\((https:\/\/[^)\s]+)\)$/)
    if (link) return <a key={index} href={link[2]} target="_blank" rel="noopener noreferrer" className="text-primary-700 underline underline-offset-2">{link[1]}</a>
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>
    if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) return <em key={index}>{part.slice(1, -1)}</em>
    return <React.Fragment key={index}>{part}</React.Fragment>
  })
}

/** Renders the small, safe Markdown subset supported by vacancy descriptions. */
export function FormattedText({ text, className = '' }: { text?: string | null; className?: string }) {
  const lines = (text || '').replace(/\r\n?/g, '\n').split('\n')
  const blocks: React.ReactNode[] = []
  let paragraph: string[] = []
  let list: { ordered: boolean; items: string[] } | null = null

  const flushParagraph = () => {
    if (!paragraph.length) return
    blocks.push(<p key={`p-${blocks.length}`} className="mb-4 last:mb-0">{paragraph.map((line, index) => <React.Fragment key={index}>{index > 0 && <br/>}{renderInline(line)}</React.Fragment>)}</p>)
    paragraph = []
  }
  const flushList = () => {
    if (!list) return
    const List = list.ordered ? 'ol' : 'ul'
    blocks.push(<List key={`list-${blocks.length}`} className={`mb-4 space-y-1 pl-6 last:mb-0 ${list.ordered ? 'list-decimal' : 'list-disc'}`}>{list.items.map((item, index) => <li key={index}>{renderInline(item)}</li>)}</List>)
    list = null
  }

  lines.forEach((rawLine) => {
    const line = rawLine.trim()
    if (!line) { flushParagraph(); flushList(); return }
    const heading = line.match(/^(#{1,3})\s+(.+)$/)
    if (heading) {
      flushParagraph(); flushList()
      const level = (heading[1] || '').length
      const Tag = level === 1 ? 'h3' : level === 2 ? 'h4' : 'h5'
      blocks.push(<Tag key={`h-${blocks.length}`} className="mb-2 mt-5 font-semibold first:mt-0">{renderInline(heading[2] || '')}</Tag>)
      return
    }
    const bullet = line.match(/^[-*•]\s+(.+)$/)
    const numbered = line.match(/^\d+[.)]\s+(.+)$/)
    if (bullet || numbered) {
      flushParagraph()
      const ordered = Boolean(numbered)
      if (list && list.ordered !== ordered) flushList()
      if (!list) list = { ordered, items: [] }
      list.items.push(bullet?.[1] ?? numbered?.[1] ?? '')
      return
    }
    flushList()
    paragraph.push(rawLine.trimEnd())
  })
  flushParagraph(); flushList()
  return <div className={`whitespace-normal break-words leading-7 ${className}`}>{blocks}</div>
}
